import { useCallback, useEffect, useState } from "react";
import { adminFetch } from "utils/adminFetch";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function AdminClientSuggestions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/client-suggestions");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load suggestions.");
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <div className="mt-3 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Client suggestions
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Ideas submitted from the client dashboard (Support → Suggestions).
          </p>
        </div>
        <button
          type="button"
          onClick={loadRows}
          disabled={loading}
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
          {error}
          {error.includes("client_suggestions") || error.includes("42P01") ? (
            <span className="mt-2 block">
              Run{" "}
              <code className="rounded bg-white/60 px-1 dark:bg-black/30">
                backend/sql/client_suggestions.sql
              </code>{" "}
              in Supabase SQL Editor.
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-[20px] border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-white/10">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Client ID</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No suggestions yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 align-top dark:border-white/5"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                    {formatWhen(row.createdAt)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                    {row.clientName}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {row.email}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-400">
                    {row.clientId}
                  </td>
                  <td className="max-w-[160px] px-4 py-3 text-gray-700 dark:text-gray-300">
                    {row.title || "—"}
                  </td>
                  <td className="max-w-md px-4 py-3 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {row.message}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
